/**
 * Step Initialization Utilities
 * Pure functions for handling step initialization and completion logic
 */

import type { Dispatch } from '@reduxjs/toolkit';

import type { RootState } from '../../../services/serviceTypes';
import { TIMEOUTS } from '../constants/steps';
import type { FieldConfig } from '../data-layer/types';
import {
  addCompletedStep,
  addSkippedStep,
  addVisitedStep,
} from '../data-store/copilotSlice';
import {
  createEditWelcomeMessageAction,
  createWelcomeMessageAction,
} from '../services/messageService';

import { showGeneralForm } from './stepFormUtils';
import isStepCompleted from './stepValidationUtils';
import { safeTimeout } from './timeoutUtils';

/**
 * Check if a step should be marked as skipped based on default values
 */
const shouldSkipStep = (
  stepName: string,
  stepFormData: Record<string, unknown>,
  fieldConfigs: FieldConfig[] | FieldConfig
): boolean => {
  if (
    stepName.toLowerCase().includes('account') ||
    stepName.toLowerCase().includes('access')
  ) {
    // Handle single object for account/access steps
    if (!Array.isArray(fieldConfigs)) {
      return !fieldConfigs.attributeValue || fieldConfigs.attributeValue === '';
    }

    // Handle array for other cases
    return fieldConfigs.some(
      (config) => !config.attributeValue || config.attributeValue === ''
    );
  }

  return false;
};

/**
 * Check completion status for all steps and mark completed/skipped ones
 */
export const checkAllStepsCompletion = (
  dispatch: Dispatch,
  getState: () => unknown
): void => {
  // CLOSURE FIX: Call getState() here to get fresh state, not cached state from closure
  const state = getState() as RootState;
  const steps = Object.keys(state.copilot.parsedTemplates ?? {});

  steps.forEach((stepName) => {
    const stepFormData = (state.copilot.stepFormData[stepName] ?? {}) as Record<
      string,
      unknown
    >;
    const fieldConfigs = (state.copilot.parsedTemplates[stepName] ?? []) as
      | FieldConfig[]
      | FieldConfig;

    // Check if step is already completed - don't override manual completion
    const isAlreadyCompleted =
      state.copilot.navigation.completedSteps.includes(stepName);

    if (isAlreadyCompleted) {
      // Skip evaluation for already completed steps
      return;
    }

    if (shouldSkipStep(stepName, stepFormData, fieldConfigs)) {
      dispatch(addSkippedStep(stepName));
    } else if (isStepCompleted(stepName, stepFormData, fieldConfigs)) {
      dispatch(addCompletedStep(stepName));
    }
  });
};

/**
 * Initialize edit mode - mark all steps as visited and show forms
 */
export const initializeEditMode = (
  dispatch: Dispatch,
  getState: () => unknown,
  nameToLabelMap: Record<string, string>
): void => {
  const allSteps = Object.keys(nameToLabelMap);

  allSteps.forEach((stepName) => {
    dispatch(addVisitedStep(stepName));
  });

  // Delay checkAllStepsCompletion to allow template parsing to complete
  safeTimeout(() => {
    checkAllStepsCompletion(dispatch, getState);
  }, 500);

  // Check if welcome message already exists
  const state = getState() as {
    copilot?: { messages?: { subType?: string }[] };
  };
  const hasWelcomeMessage = state.copilot?.messages?.some(
    (msg) => msg.subType === 'start'
  );

  if (!hasWelcomeMessage) {
    dispatch(createEditWelcomeMessageAction());
  }
  // Don't show any forms in edit mode - let user choose which step to work on
};

/**
 * Initialize create mode - show welcome message and delayed general form
 */
export const initializeCreateMode = (
  dispatch: Dispatch,
  getState?: () => unknown
): void => {
  // Check if welcome message already exists
  const state = getState?.() as {
    copilot?: { messages?: { subType?: string }[] };
  };
  const hasWelcomeMessage = state.copilot?.messages?.some(
    (msg) => msg.subType === 'start'
  );

  if (!hasWelcomeMessage) {
    dispatch(createWelcomeMessageAction());
  }

  safeTimeout(
    () => showGeneralForm(dispatch, true, {}, getState),
    TIMEOUTS.GENERAL_FORM_DELAY
  );
};
